import { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Scatter, ComposedChart } from 'recharts';
import { fmtDate } from '@/utils/dateFmt';
import { fmtNum } from '@/utils/numFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const CURVE_COLORS = { P3: '#C9B8A6', P15: '#A8B5A0', P50: '#657161', P85: '#A8B5A0', P97: '#C9B8A6' };
const NAMES = ['P3', 'P15', 'P50', 'P85', 'P97'];
const interp = (rows, x) => {
  if (!rows.length) return {};
  const j = rows.findIndex(r => r.x >= x);
  if (j <= 0) return rows[Math.max(j, 0)] || rows[rows.length - 1];
  const a = rows[j - 1], b = rows[j], t = b.x === a.x ? 0 : (x - a.x) / (b.x - a.x);
  return Object.fromEntries(NAMES.map(n => [n, a[n] + (b[n] - a[n]) * t]));
};
const GrowthTooltip = ({ active, payload, label, isLen, pts, chartLabel }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const exact = row.child !== undefined ? pts.find(p => p.x === row.x) : null;
  const near = exact || pts.reduce((b, p) => (b === null || Math.abs(p.x - row.x) < Math.abs(b.x - row.x) ? p : b), null);
  return (
    <div className="bg-white border border-brand-border rounded-xl p-2 text-[11px] shadow-sm max-w-[200px] whitespace-normal" data-testid="growth-tooltip">
      <p className="font-medium text-brand-text mb-1">{isLen ? `${fmtNum(Math.round(label * 10) / 10)} cm` : `${fmtNum(Math.round(label * 10) / 10)} meses`}</p>
      {near && <p className="mb-1" style={{ color: '#B7791F' }} data-testid="growth-tooltip-child">{chartLabel}: <b>{fmtNum(near.y)}</b>{exact ? '' : ` (medição de ${fmtDate(near.date)})`} · P{fmtNum(near.percentile)}</p>}
      {NAMES.map(n => <p key={n} className="text-brand-muted" data-testid={`growth-tooltip-${n}`}>{n}: {fmtNum(row[n], 1)}</p>)}
    </div>
  );
};
const TAG = (c) => (/baixa|baixo|Magreza|muito/i.test(c) ? 'bg-amber-50 text-amber-700' : /elevad|Excesso|Obesidade|Risco/i.test(c) ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700');

export function PediatricGrowthPanel({ patientId, refreshKey }) {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState('');
  useEffect(() => {
    axios.get(`${API}/api/patients/${patientId}/pediatric-growth`, { withCredentials: true }).then(({ data: d }) => {
      setData(d);
      const ks = Object.keys(d.points || {});
      setKind(k => (ks.includes(k) ? k : ks[0] || ''));
    }).catch(() => setData(null));
  }, [patientId, refreshKey]);

  if (!data || data.eligible === false) return null;
  if (data.eligible === null) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 text-sm text-amber-800" data-testid="pediatric-growth-missing">
        Para calcular percentis e z-scores pediátricos, falta preencher na ficha {data.missing.join(' e ')}.
      </div>
    );
  }
  const evals = [...(data.evaluations || [])].reverse();
  const latest = evals[0];
  if (!latest) return null;
  const pts = (data.points[kind] || []);
  const curve = data.curves[kind];
  const rows = curve ? curve.x.map((x, i) => ({ x, P3: curve.P3[i], P15: curve.P15[i], P50: curve.P50[i], P85: curve.P85[i], P97: curve.P97[i] })) : [];
  const isLen = kind === 'wfl' || kind === 'wfh';
  const px = pts.map(p => p.x);
  const lo = Math.min(...px), hi = Math.max(...px);
  const span = isLen ? 12 : 18;
  const view = rows.filter(r => r.x >= lo - span && r.x <= hi + span);
  const merged = [...view, ...pts.map(p => ({ x: p.x, child: p.y, ...interp(rows, p.x) }))].sort((a, b) => a.x - b.x);
  const lastIdx = merged.length - 1;
  const label = latest.indicators.find(i => i.kind === kind)?.label || '';
  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4" data-testid="pediatric-growth-panel">
      <h3 className="font-heading font-medium text-brand-text mb-1">Crescimento (OMS)</h3>
      <p className="text-[11px] text-brand-muted mb-3">Avaliação de {fmtDate(latest.date)}. {data.source}.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="pediatric-growth-table">
          <thead><tr className="text-[10px] uppercase tracking-wider text-brand-muted"><th className="text-left py-1">Indicador</th><th className="text-right">Valor</th><th className="text-right">Z-score</th><th className="text-right">Percentil</th><th className="text-left pl-3">Classificação OMS</th></tr></thead>
          <tbody>{latest.indicators.map(i => (
            <tr key={i.kind} className="border-t border-brand-border/40" data-testid={`growth-row-${i.kind}`}>
              <td className="py-1.5">{i.label}</td><td className="text-right">{fmtNum(i.value)} {i.unit}</td><td className="text-right">{fmtNum(i.z)}</td><td className="text-right">P{fmtNum(i.percentile)}</td>
              <td className="pl-3"><span className={`text-xs px-2 py-0.5 rounded-full ${TAG(i.classification)}`}>{i.classification}</span></td>
            </tr>))}</tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-4 mb-2">
        {Object.keys(data.points).map(k => (
          <button key={k} onClick={() => setKind(k)} className={`text-xs px-3 py-1 rounded-full ${k === kind ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`growth-chart-tab-${k}`}>{latest.indicators.find(i => i.kind === k)?.label}</button>
        ))}
      </div>
      <div style={{ width: '100%', height: 280 }} data-testid="pediatric-growth-chart">
        <ResponsiveContainer>
          <ComposedChart data={merged} margin={{ top: 8, right: 30, left: -8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="x" type="number" tickFormatter={(v) => fmtNum(v)} domain={['dataMin', 'dataMax']} tick={{ fontSize: 10 }} label={{ value: isLen ? 'Comprimento/estatura (cm)' : 'Idade (meses)', position: 'insideBottom', offset: -2, fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} tickFormatter={(v) => fmtNum(v)} />
            <Tooltip content={<GrowthTooltip isLen={isLen} pts={pts} chartLabel={label} />} cursor={{ stroke: '#657161', strokeDasharray: '3 3' }} />
            {NAMES.map(n => <Line key={n} dataKey={n} dot={false} strokeWidth={n === 'P50' ? 2 : 1} stroke={CURVE_COLORS[n]} strokeDasharray={n === 'P50' ? '' : '4 3'} connectNulls name={n} isAnimationActive={false} label={(p) => (p.index === lastIdx ? <text key={n} x={p.x + 4} y={p.y} dy={3} fontSize={9} fill={CURVE_COLORS[n] === '#C9B8A6' ? '#9A8470' : '#657161'} data-testid={`growth-curve-label-${n}`}>{n}</text> : null)} />)}
            <Line dataKey="child" stroke="#B7791F" strokeWidth={2} dot={{ r: 4, fill: '#B7791F' }} connectNulls name="child" isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-brand-muted mt-1">Curvas P3, P15, P50, P85 e P97. Pontos a dourado: medições da criança.</p>
    </div>
  );
}
